import * as i0 from '@angular/core';
import { InjectionToken, inject, InjectFlags, Optional, Inject, Injectable, NgModule, SkipSelf } from '@angular/core';
import * as i5 from '@ngx-matomo/tracker';
import { INTERNAL_MATOMO_CONFIGURATION, MatomoTracker } from '@ngx-matomo/tracker';
import * as i1 from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { of, identity, combineLatest } from 'rxjs';
import { delay, filter, distinctUntilKeyChanged, switchMap, map } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';

import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/router';
import * as ɵngcc2 from '@ngx-matomo/tracker';
const MATOMO_ROUTER_CONFIGURATION = new InjectionToken('MATOMO_ROUTER_CONFIGURATION');
const DEFAULT_ROUTER_CONFIGURATION = {
    prependBaseHref: true,
    trackPageTitle: true,
    delay: 0,
    exclude: [],
};
const INTERNAL_ROUTER_CONFIGURATION = new InjectionToken('INTERNAL_ROUTER_CONFIGURATION', {
    factory: () => {
        const { disabled, enableLinkTracking } = inject(INTERNAL_MATOMO_CONFIGURATION);
        const routerConfig = inject(MATOMO_ROUTER_CONFIGURATION, InjectFlags.Optional) || {};
        return Object.assign(Object.assign(Object.assign({}, DEFAULT_ROUTER_CONFIGURATION), routerConfig), { enableLinkTracking, disabled });
    },
});

const MATOMO_PAGE_TITLE_PROVIDER = new InjectionToken('MATOMO_PAGE_TITLE_PROVIDER', {
    factory: () => new DefaultPageTitleProvider(inject(Title)),
});
class DefaultPageTitleProvider {
    constructor(title) {
        this.title = title;
    }
    getCurrentPageTitle(event) {
        return of(this.title.getTitle());
    }
}

const MATOMO_PAGE_URL_PROVIDER = new InjectionToken('MATOMO_PAGE_URL_PROVIDER', {
    factory: () => new DefaultPageUrlProvider(inject(INTERNAL_ROUTER_CONFIGURATION), inject(APP_BASE_HREF, InjectFlags.Optional), inject(PlatformLocation)),
});
function trimTrailingSlash(str) {
    return str.endsWith('/') ? str.slice(0, -1) : str;
}
class DefaultPageUrlProvider {
    constructor(config, baseHref, platformLocation) {
        this.config = config;
        this.baseHref = baseHref;
        this.platformLocation = platformLocation;
    }
    getCurrentPageUrl(event) {
        const url = this.config.prependBaseHref
            ? this.getBaseHrefWithoutTrailingSlash() + event.urlAfterRedirects
            : event.urlAfterRedirects;
        return of(url);
    }
    getBaseHrefWithoutTrailingSlash() {
        var _a;
        return trimTrailingSlash((_a = this.baseHref) !== null && _a !== void 0 ? _a : this.platformLocation.getBaseHrefFromDOM());
    }
}
DefaultPageUrlProvider.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [INTERNAL_ROUTER_CONFIGURATION,] }] },
    { type: String, decorators: [{ type: Optional }, { type: Inject, args: [APP_BASE_HREF,] }] },
    { type: PlatformLocation }
];

function isNavigationEnd(event) {
    return event instanceof NavigationEnd;
}
function coerceRegExp(input) {
    return typeof input === 'string' ? new RegExp(input) : input;
}
function coerceRegExpArray(input) {
    if (!input) {
        return [];
    }
    return Array.isArray(input) ? input.map(coerceRegExp) : [coerceRegExp(input)];
}
function isNotExcluded(excludeConfig) {
    const exclusions = coerceRegExpArray(excludeConfig);
    return (event) => !exclusions.some(rx => event.urlAfterRedirects.match(rx));
}
class MatomoRouter {
    constructor(router, config, pageTitleProvider, pageUrlProvider, tracker) {
        this.router = router;
        this.config = config;
        this.pageTitleProvider = pageTitleProvider;
        this.pageUrlProvider = pageUrlProvider;
        this.tracker = tracker;
    }
    init() {
        if (this.config.disabled) {
            // Do not set-up router if globally disabled
            return;
        }
        const delayOp = this.config.delay === -1 ? identity : delay(this.config.delay);
        this.router.events
            .pipe(
        // Take only NavigationEnd events
        filter(isNavigationEnd), 
        // Filter out excluded urls
        filter(isNotExcluded(this.config.exclude)), 
        // Distinct urls
        distinctUntilKeyChanged('urlAfterRedirects'), 
        // Optionally add some delay
        delayOp, 
        // Grab page title & url
        switchMap(event => this.getPageTitleAndUrl(event)))
            .subscribe(({ pageTitle, pageUrl }) => this.trackPageView(pageTitle, pageUrl));
    }
    getPageTitleAndUrl(event) {
        const title$ = this.config.trackPageTitle
            ? this.pageTitleProvider.getCurrentPageTitle(event)
            : of(undefined);
        const url$ = this.pageUrlProvider.getCurrentPageUrl(event);
        return combineLatest([title$, url$]).pipe(map(([pageTitle, pageUrl]) => ({ pageTitle, pageUrl })));
    }
    trackPageView(pageTitle, pageUrl) {
        this.tracker.setCustomUrl(pageUrl);
        this.tracker.trackPageView(pageTitle);
        if (this.config.enableLinkTracking) {
            this.tracker.enableLinkTracking(true);
        }
        // Set referrer for next page view
        this.tracker.setReferrerUrl(pageUrl);
    }
}
MatomoRouter.ɵfac = function MatomoRouter_Factory(t) { return new (t || MatomoRouter)(ɵngcc0.ɵɵinject(ɵngcc1.Router), ɵngcc0.ɵɵinject(INTERNAL_ROUTER_CONFIGURATION), ɵngcc0.ɵɵinject(MATOMO_PAGE_TITLE_PROVIDER), ɵngcc0.ɵɵinject(MATOMO_PAGE_URL_PROVIDER), ɵngcc0.ɵɵinject(ɵngcc2.MatomoTracker)); };
MatomoRouter.ɵprov = i0.ɵɵdefineInjectable({ factory: function MatomoRouter_Factory() { return new MatomoRouter(i0.ɵɵinject(i1.Router), i0.ɵɵinject(INTERNAL_ROUTER_CONFIGURATION), i0.ɵɵinject(MATOMO_PAGE_TITLE_PROVIDER), i0.ɵɵinject(MATOMO_PAGE_URL_PROVIDER), i0.ɵɵinject(i5.MatomoTracker)); }, token: MatomoRouter, providedIn: "root" });
MatomoRouter.ctorParameters = () => [
    { type: Router },
    { type: undefined, decorators: [{ type: Inject, args: [INTERNAL_ROUTER_CONFIGURATION,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [MATOMO_PAGE_TITLE_PROVIDER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [MATOMO_PAGE_URL_PROVIDER,] }] },
    { type: MatomoTracker }
];
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(MatomoRouter, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], function () { return [{ type: ɵngcc1.Router }, { type: undefined, decorators: [{
                type: Inject,
                args: [INTERNAL_ROUTER_CONFIGURATION]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [MATOMO_PAGE_TITLE_PROVIDER]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [MATOMO_PAGE_URL_PROVIDER]
            }] }, { type: ɵngcc2.MatomoTracker }]; }, null); })();

class NgxMatomoRouterModule {
    constructor(router, parent) {
        this.router = router;
        if (!parent) {
            // Do not initialize if it is already (by a parent module)
            this.router.init();
        }
    }
    static forRoot(config = {}) {
        return {
            ngModule: NgxMatomoRouterModule,
            providers: [{ provide: MATOMO_ROUTER_CONFIGURATION, useValue: config }],
        };
    }
}
NgxMatomoRouterModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: NgxMatomoRouterModule });
NgxMatomoRouterModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function NgxMatomoRouterModule_Factory(t) { return new (t || NgxMatomoRouterModule)(ɵngcc0.ɵɵinject(MatomoRouter), ɵngcc0.ɵɵinject(NgxMatomoRouterModule, 12)); } });
NgxMatomoRouterModule.ctorParameters = () => [
    { type: MatomoRouter },
    { type: NgxMatomoRouterModule, decorators: [{ type: Optional }, { type: SkipSelf }] }
];
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(NgxMatomoRouterModule, [{
        type: NgModule
    }], function () { return [{ type: MatomoRouter }, { type: NgxMatomoRouterModule, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();

/*
 * Public API Surface of router
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MATOMO_PAGE_TITLE_PROVIDER, MATOMO_PAGE_URL_PROVIDER, MATOMO_ROUTER_CONFIGURATION, NgxMatomoRouterModule, INTERNAL_ROUTER_CONFIGURATION as ɵb, MatomoRouter as ɵc };

//# sourceMappingURL=ngx-matomo-router.js.map